import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../backend/convex/_generated/api";
import { WelcomeModal } from '@/components/WelcomeModal';
import { AuthContext, AuthUser } from '@/context/auth-context';

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user: clerkUser, isLoaded: isClerkLoaded } = useUser();
  const { signOut, openSignIn } = useClerk();
  const createOrUpdateUser = useMutation(api.auth.createOrUpdateClerkUser);
  const grantSignupBonus = useMutation(api.stripe.grantSignupBonus);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const hasCheckedSignupBonusRef = useRef(false);
  
  // Fetch user profile from Convex using Clerk ID
  const convexUser = useQuery(
    api.auth.getUserByClerkId,
    clerkUser ? {
      clerkId: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress,
      name: clerkUser.fullName || undefined,
      image: clerkUser.imageUrl || undefined,
    } : "skip"
  );

  // Create or update user profile when Clerk user is available but no Convex profile exists
  useEffect(() => {
    if (clerkUser && convexUser === null) {
      createOrUpdateUser({
        clerkId: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress,
        name: clerkUser.fullName || undefined,
        image: clerkUser.imageUrl || undefined,
      });
    }
  }, [clerkUser, convexUser, createOrUpdateUser]);

  useEffect(() => {
    hasCheckedSignupBonusRef.current = false;
  }, [clerkUser?.id]);

  // Grant signup bonus when user is created and has no credits
  useEffect(() => {
    if (clerkUser && convexUser && !hasCheckedSignupBonusRef.current) {
      hasCheckedSignupBonusRef.current = true;
      
      // Check if user has no credits (new user)
      if (convexUser.tokenBalance === 0) {
        // Grant 100 coin signup bonus
        grantSignupBonus({
          clerkId: clerkUser.id,
        }).then((result) => {
          if (result.success && !result.alreadyGranted) {
            console.log('Granted 100 coin signup bonus');
            setShowWelcomeModal(true);
          }
        }).catch((err: unknown) => {
          console.error('Failed to grant signup bonus:', err);
        });
      }
    }
  }, [clerkUser, convexUser, grantSignupBonus]);

  const isLoading = !isClerkLoaded || (clerkUser !== null && convexUser === undefined);
  
  const user: AuthUser | null = clerkUser && convexUser ? {
    id: clerkUser.id,
    email: clerkUser.primaryEmailAddress?.emailAddress,
    name: clerkUser.fullName || undefined,
    image: clerkUser.imageUrl || undefined,
    tokenBalance: convexUser.tokenBalance,
    role: convexUser.role,
  } : null;

  const signIn = () => {
    openSignIn();
  };

  const logout = async () => {
    await signOut();
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      isAdmin: user?.role === "admin",
      signIn,
      logout,
    }}>
      {children}
      <WelcomeModal 
        isOpen={showWelcomeModal} 
        onClose={() => setShowWelcomeModal(false)} 
      />
    </AuthContext.Provider>
  );
}
